import React, { useMemo } from "react";
import { Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  UserAddOutlined,
  FileAddOutlined,
  ShoppingCartOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  ContactsOutlined,
} from "@ant-design/icons";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";

// Shortcuts to the "create new" page for each major module. Gating mirrors
// Sidebar.js's own two-layer filter exactly (module-available, then
// per-action WRITE permission) rather than inventing a new access model.
const SHORTCUTS = [
  {
    key: "customer_entry",
    label: "New Customer",
    icon: <UserAddOutlined />,
    path: "/customers/addNewCustomer",
    accessKey: "customer_entry",
    moduleKey: "sales",
  },
  {
    key: "new_sales",
    label: "New Sales Invoice",
    icon: <FileAddOutlined />,
    path: "/invoice",
    accessKey: "new_sales",
    moduleKey: "sales",
  },
  {
    key: "purchase_entry",
    label: "New Purchase Entry",
    icon: <ShoppingCartOutlined />,
    path: "/purchase",
    accessKey: "purchase_entry",
    moduleKey: "purchase",
  },
  {
    key: "sales_order_entry",
    label: "New Sales Order",
    icon: <FileDoneOutlined />,
    path: "/order/sales",
    accessKey: "sales_order_entry",
    moduleKey: "order",
  },
  {
    key: "quotation_entry",
    label: "New Quotation",
    icon: <FileSearchOutlined />,
    path: "/quotation/quotation",
    accessKey: "quotation_entry",
    moduleKey: "quotation",
  },
  {
    key: "crm_leads",
    label: "New CRM Lead",
    icon: <ContactsOutlined />,
    path: "/crm/leads",
    accessKey: "crm_leads",
    moduleKey: "crm",
  },
];

const DashboardQuickActions = () => {
  const navigate = useNavigate();
  const authInfo = useSelector(selectAuthInfo);
  const isAdminUser = useIsAdminUser();

  const shortcuts = useMemo(() => {
    if (isAdminUser) return SHORTCUTS;

    return SHORTCUTS.filter((s) => {
      const moduleAvailable =
        !authInfo?.disabled_modules?.includes(s.moduleKey) &&
        authInfo?.tab?.some((t) => t.tab === s.moduleKey);
      return moduleAvailable && getAccessType(s.accessKey) === "WRITE";
    });
  }, [authInfo, isAdminUser]);

  if (!shortcuts.length) return null;

  return (
    <div className="erp-quick-actions-row">
      {shortcuts.map((s) => (
        <Tooltip key={s.key} title={s.label}>
          <button
            type="button"
            className="erp-quick-action"
            onClick={() => navigate(s.path)}
          >
            <span className="erp-quick-action-icon">{s.icon}</span>
            <span className="erp-quick-action-label">{s.label}</span>
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

export default DashboardQuickActions;
